import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import { Box, useTheme } from '@mui/material';
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import type { Country } from 'src/api/zrm';
import { formatCount, formatWeekday } from 'src/tv/dashboardFormat';
import type { MbView } from 'src/tv/dashboardView';
import { TV_COLORS } from 'src/tv/tvTheme';

import type { ChartData, ChartOptions, ChartType, Plugin } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement);

interface WeekMarksOptions {
  /** The daily goal, drawn as a dashed line across the chart; null draws none. */
  goal: number | null;
  goalLabel: string;
  /** The bar of today, whose value label sits on the accent fill. */
  todayIndex: number;
  fontFamily: string;
}

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends ChartType> {
    tvWeekMarks?: WeekMarksOptions;
  }
}

/** The chart sizes its type like the rest of the screen: in viewport units, so it reads the same at 1080p and 4K. */
const vw = (value: number) => (window.innerWidth * value) / 100;
const vh = (value: number) => (window.innerHeight * value) / 100;

/**
 * Share of a day's column the bar fills; the rest is the 1vw gap between days. The canvas is 1vw wider
 * than its grid column (half a gap each side, see the box below), so the first and last bars sit flush
 * with the column. On the dense TV frame the column is 29.47vw: (29.47 + 1) / 5 = 6.09vw per day.
 */
const BAR_SHARE = 5.09 / 6.09;
/** Head room over the tallest bar (or the goal) for the value label above a short bar. */
const SCALE_HEADROOM = 1.12;
const GOAL_FONT_VW = 0.85;
const GOAL_GAP_VH = 0.3;
/**
 * Room over the plot for the goal label, which sits above its line even when the goal tops the scale. It
 * reaches up into the row gap, so the plot itself starts level with the figures beside it.
 */
const LABEL_ROOM_VH = 2;
/** The chart's height in the grid row, weekday labels included. */
const CHART_VH = 13.5;
const TODAY_FILL = TV_COLORS.accent;
const PAST_FILL = 'rgba(75, 163, 222, 0.38)';

/** A dashed outline open at the bottom, for a day still to come (not a zero). */
const strokeEmptySlot = (ctx: CanvasRenderingContext2D, left: number, right: number, top: number, bottom: number, radius: number) => {
  ctx.beginPath();
  ctx.moveTo(left, bottom);
  ctx.lineTo(left, top + radius);
  ctx.arcTo(left, top, left + radius, top, radius);
  ctx.lineTo(right - radius, top);
  ctx.arcTo(right, top, right, top + radius, radius);
  ctx.lineTo(right, bottom);
  ctx.stroke();
};

/**
 * Draws what chart.js has no option for: the baseline and the daily-goal line with its label (both
 * from the first bar's left edge to the last bar's right edge, not across the half gaps at the canvas
 * ends), the empty slots of the days to come, and each day's count on its bar (inside the top when the
 * bar is tall enough, so the goal line never runs through it; above it otherwise).
 */
export const weekMarks: Plugin<'bar', WeekMarksOptions> = {
  id: 'tvWeekMarks',
  afterDatasetsDraw: (chart, _args, options) => {
    const { ctx, chartArea, scales } = chart;
    const values = chart.data.datasets[0].data as Array<number | null>;
    const scaleMax = scales.y.max;
    const bars = chart.getDatasetMeta(0).data.map((bar) => bar.getProps(['x', 'y', 'width'], true) as { x: number; y: number; width: number });

    if (!bars.length) return;

    const left = Math.round(bars[0].x - bars[0].width / 2);
    const right = Math.round(bars[bars.length - 1].x + bars[bars.length - 1].width / 2);
    const baseline = Math.round(chartArea.bottom) + 0.5;

    ctx.save();

    ctx.strokeStyle = TV_COLORS.muted;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left, baseline);
    ctx.lineTo(right, baseline);
    ctx.stroke();

    if (options.goal) {
      const y = Math.round(scales.y.getPixelForValue(options.goal)) + 0.5;

      ctx.strokeStyle = TV_COLORS.muted;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = TV_COLORS.muted;
      ctx.font = `400 ${vw(GOAL_FONT_VW)}px ${options.fontFamily}`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(options.goalLabel, right, y - vh(GOAL_GAP_VH));
    }

    ctx.font = `700 ${vw(1.3)}px ${options.fontFamily}`;
    ctx.textAlign = 'center';

    bars.forEach(({ x, y, width }, index) => {
      const value = values[index];

      if (value === null) {
        ctx.strokeStyle = TV_COLORS.line;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        strokeEmptySlot(ctx, Math.round(x - width / 2) + 0.5, Math.round(x + width / 2) - 0.5, chartArea.top + 0.5, chartArea.bottom, vw(0.3));
        ctx.setLineDash([]);

        return;
      }

      const isInside = value / scaleMax >= 0.3;

      ctx.fillStyle = isInside && index === options.todayIndex ? TV_COLORS.background : TV_COLORS.text;
      ctx.textBaseline = isInside ? 'top' : 'bottom';
      ctx.fillText(formatCount(value), x, isInside ? y + vh(0.5) : y - vh(0.3));
    });

    ctx.restore();
  },
};

/** Changes when the window is resized, so the px sizes computed from vw/vh are computed again. */
const useViewportKey = () => {
  const [key, setKey] = useState(() => `${window.innerWidth}x${window.innerHeight}`);

  useEffect(() => {
    const onResize = () => setKey(`${window.innerWidth}x${window.innerHeight}`);

    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  return key;
};

interface WeekChartProps {
  view: MbView;
  country: Country;
}

/** Monday to Friday as columns, today in the accent, days to come as empty slots; a line marks the daily goal. */
const WeekChart: FC<WeekChartProps> = (props) => {
  const { view, country } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { weekdays, weekdayScale, dayGoal } = view;
  const fontFamily = theme.typography.fontFamily ?? 'sans-serif';
  const goalLabel = t('goal {{goal}}', { goal: formatCount(dayGoal) });
  const viewportKey = useViewportKey();

  const data = useMemo<ChartData<'bar', Array<number | null>>>(() => ({
    labels: weekdays.map((day) => formatWeekday(day.date, country)),
    datasets: [{
      // A day still to come has no bar; the plugin draws its empty slot.
      data: weekdays.map((day) => (day.isAhead ? null : day.apps)),
      backgroundColor: weekdays.map((day) => (day.isToday ? TODAY_FILL : PAST_FILL)),
      borderRadius: () => vw(0.3),
      borderSkipped: 'start',
      minBarLength: 2,
      categoryPercentage: 1,
      barPercentage: BAR_SHARE,
    }],
  }), [weekdays, country]);

  const options = useMemo<ChartOptions<'bar'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    // A wall screen: nothing to hover, and nothing should move when the numbers refresh.
    animation: false,
    events: [],
    layout: { padding: { top: vh(LABEL_ROOM_VH) } },
    scales: {
      x: {
        grid: { display: false, drawTicks: false },
        // The plugin draws the baseline, under the bars only.
        border: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          padding: vh(0.6),
          color: (context) => (weekdays[context.index]?.isToday ? TV_COLORS.text : TV_COLORS.muted),
          font: (context) => ({ family: fontFamily, size: vw(1), weight: weekdays[context.index]?.isToday ? 700 : 500 }),
        },
      },
      y: { display: false, min: 0, max: weekdayScale * SCALE_HEADROOM },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      tvWeekMarks: { goal: dayGoal, goalLabel, todayIndex: weekdays.findIndex((day) => day.isToday), fontFamily },
    },
    // viewportKey: the paddings above are px from the viewport, and must follow a resize.
  }), [weekdays, weekdayScale, dayGoal, goalLabel, fontFamily, viewportKey]);

  if (!weekdays.length) return <Box />;

  return (
    <Box
      sx={{
        position: 'relative',
        height: `${CHART_VH + LABEL_ROOM_VH}vh`,
        mt: `-${LABEL_ROOM_VH}vh`,
        mx: '-0.5vw',
        minWidth: 0,
      }}
    >
      <Bar
        data={data}
        options={options}
        plugins={[weekMarks]}
        role="img"
        aria-label={t('Applications per day this week')}
      />
    </Box>
  );
};

export default WeekChart;
